/**
 * DOM part of the scroll spy of the sectioned dialogs (edit dialogs, CreateStepperDialog, createInstitutionDialog,
 * employeeContractForm). Sections are the elements with `data-section="<id>"`; they are read from the DOM on every
 * update, so conditional sections (v-if / v-show) just work. The rules themselves: sectionScrollSpy.helper.ts.
 *
 * Usage in a component:
 *   created() { this.spy = createScrollSpy({scroller: () => this.scroller(), onChange: (id) => { this.activeSection = id; }}); }
 *   beforeDestroy() { this.spy?.destroy(); }
 *   template: <v-card-text ref="scroller" @scroll="onScroll" @focusin="onFocusIn" @focusout="onFocusOut"> + scrollTo(id) in the nav
 * The returned object is frozen, so it can live in `data` without Vue making it reactive.
 */
import {computeActiveSection, SpySection} from '@/helpers/sectionScrollSpy.helper';

export interface ScrollSpyOptions {
  /** The scrolling element (e.g. the .mdlg-body / v-card-text) */
  scroller: () => HTMLElement | null | undefined;
  /** Element whose [data-section] children count (default: the scroller) */
  root?: () => Element | null | undefined;
  /** Called with the id of the new active section */
  onChange: (id: string) => void;
  /** Covered height at the top of the scroller (sticky tabs), also kept free when jumping to a section */
  topOffset?: () => number;
}

export interface ScrollSpy {
  /** Smooth scroll to the section; it stays active until the scrolling has ended and the user scrolls again */
  scrollTo(id: string): boolean;
  onScroll(): void;
  onFocusIn(event: FocusEvent): void;
  onFocusOut(event: FocusEvent): void;
  /** Re-evaluate (e.g. after sections appeared / disappeared) */
  refresh(): void;
  /** Forget focus and jump lock (dialog opened again) */
  reset(): void;
  destroy(): void;
}

/** No scroll event for this long = the (smooth) scroll after a navigation click has ended */
const SCROLL_END_MS = 180;
/** Menus of autocompletes / selects / date pickers are attached to <body>: focus there still belongs to the field */
const DETACHED_POPUP = '.v-menu__content, .menuable__content__active, .v-picker';

function sectionId(el: Element | null | undefined, root: Element): string | undefined {
  const section = el?.closest('[data-section]') as HTMLElement | null | undefined;
  return section && root.contains(section) ? section.dataset.section || undefined : undefined;
}

export function createScrollSpy(options: ScrollSpyOptions): ScrollSpy {
  let lockedId: string | undefined;
  let lockSettled = false;
  let lockTimer: number | undefined;
  let focusTimer: number | undefined;
  let focusedId: string | undefined;
  let resizeObserver: ResizeObserver | undefined;
  let observed: Element | undefined;
  let frame: number | undefined;

  const getRoot = (): Element | undefined => (options.root ? options.root() : options.scroller()) || undefined;
  const topOffset = () => (options.topOffset ? options.topOffset() : 0);

  function measure(scroller: HTMLElement, root: Element): SpySection[] {
    const base = scroller.getBoundingClientRect().top - scroller.scrollTop;
    return (Array.from(root.querySelectorAll('[data-section]')) as HTMLElement[])
        .filter((el) => el.getClientRects().length > 0)
        .map((el) => {
          const rect = el.getBoundingClientRect();
          return {id: el.dataset.section || '', top: rect.top - base, bottom: rect.bottom - base};
        })
        .filter((section) => !!section.id);
  }

  function update() {
    const scroller = options.scroller();
    const root = getRoot();
    if (!scroller || !root) {
      return;
    }
    observe(scroller);
    const id = computeActiveSection(measure(scroller, root), scroller, {lockedId, focusedId, topOffset: topOffset()});
    if (id) {
      options.onChange(id);
    }
  }

  /** Sections appearing / disappearing change the content height: re-evaluate then (once per frame) */
  function observe(scroller: HTMLElement) {
    const content = scroller.firstElementChild || undefined;
    if (typeof ResizeObserver === 'undefined' || !content || content === observed) {
      return;
    }
    resizeObserver?.disconnect();
    resizeObserver = new ResizeObserver(() => {
      if (frame === undefined) {
        frame = window.requestAnimationFrame(() => {
          frame = undefined;
          update();
        });
      }
    });
    resizeObserver.observe(content);
    observed = content;
  }

  function clearLock() {
    window.clearTimeout(lockTimer);
    lockTimer = undefined;
    lockedId = undefined;
    lockSettled = false;
  }

  function armLockTimer() {
    window.clearTimeout(lockTimer);
    lockTimer = window.setTimeout(() => {
      lockSettled = true;
    }, SCROLL_END_MS);
  }

  return Object.freeze({
    scrollTo(id: string): boolean {
      const scroller = options.scroller();
      const root = getRoot();
      const target = root?.querySelector(`[data-section="${id}"]`) as HTMLElement | null | undefined;
      if (!scroller || !target) {
        return false;
      }
      const top = target.getBoundingClientRect().top - scroller.getBoundingClientRect().top + scroller.scrollTop;
      lockedId = id;
      lockSettled = false;
      armLockTimer();
      scroller.scrollTo({top: Math.max(0, top - topOffset() - 4), behavior: 'smooth'});
      options.onChange(id);
      return true;
    },
    onScroll() {
      if (lockedId) {
        if (!lockSettled) {
          armLockTimer();
          return;
        }
        clearLock();
      }
      update();
    },
    onFocusIn(event: FocusEvent) {
      const root = getRoot();
      if (!root) {
        return;
      }
      window.clearTimeout(focusTimer);
      focusedId = sectionId(event.target as Element | null, root);
      if (focusedId) {
        clearLock();
        update();
      }
    },
    onFocusOut(event: FocusEvent) {
      const root = getRoot();
      const related = event.relatedTarget as Element | null;
      if (!root || (related && root.contains(related))) {
        return;
      }
      window.clearTimeout(focusTimer);
      focusTimer = window.setTimeout(() => {
        const active = document.activeElement;
        if (active && root.contains(active)) {
          return;
        }
        const inPopup = !!active?.closest(DETACHED_POPUP) || (!related && !!document.querySelector('.menuable__content__active'));
        if (!inPopup) {
          focusedId = undefined;
          update();
        }
      });
    },
    refresh() {
      update();
    },
    reset() {
      clearLock();
      window.clearTimeout(focusTimer);
      focusedId = undefined;
    },
    destroy() {
      clearLock();
      window.clearTimeout(focusTimer);
      if (frame !== undefined) {
        window.cancelAnimationFrame(frame);
        frame = undefined;
      }
      resizeObserver?.disconnect();
      resizeObserver = undefined;
      observed = undefined;
    },
  });
}
